import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import apiRequest from "../../lib/http"
import { businessTimeZone } from "../../utils/date"

/**
 * Get sales dashboard metrics
 */
export const getSalesDashboardMetrics = createAsyncThunk(
  'salesDashboard/getMetrics',
  async ({ datePreset, startDate, endDate, affiliateId, contactGroupId }, { rejectWithValue }) => {
    try {
      const response = await apiRequest.get('/sales-dashboard/metrics', {
        params: {
          datePreset,
          startDate,
          endDate,
          affiliateId,
          contactGroupId,
          timezone: businessTimeZone,
        },
      })
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to fetch dashboard metrics'
      return rejectWithValue(errorMessage)
    }
  }
)

/**
 * Get affiliates for dashboard filter dropdown
 */
export const getAffiliatesForDashboard = createAsyncThunk(
  'salesDashboard/getAffiliates',
  async ({ contactGroupId }, { rejectWithValue }) => {
    try {
      const response = await apiRequest.get('/sales-dashboard/affiliates', {
        params: { contactGroupId },
      })
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to fetch affiliates'
      return rejectWithValue(errorMessage)
    }
  }
)

/**
 * Get contact groups for dashboard filter dropdown
 */
export const getContactGroupsForDashboard = createAsyncThunk(
  'salesDashboard/getContactGroups',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiRequest.get('/sales-dashboard/contact-groups')
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to fetch contact groups'
      return rejectWithValue(errorMessage)
    }
  }
)

/**
 * Get forecasting metrics (average term + payment-position clearance funnel)
 */
export const getForecasting = createAsyncThunk(
  'salesDashboard/getForecasting',
  async ({ affiliateId, contactGroupId }, { rejectWithValue }) => {
    try {
      const response = await apiRequest.get('/sales-dashboard/forecasting', {
        params: {
          affiliateId,
          contactGroupId,
          timezone: businessTimeZone,
        },
      })
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to fetch forecasting'
      return rejectWithValue(errorMessage)
    }
  }
)

/**
 * Get chart data by metric type
 */
export const getChartData = createAsyncThunk(
  'salesDashboard/getChartData',
  async ({ datePreset, startDate, endDate, affiliateId, contactGroupId, metricType }, { rejectWithValue }) => {
    try {
      const response = await apiRequest.get('/sales-dashboard/chart', {
        params: {
          datePreset,
          startDate,
          endDate,
          affiliateId,
          contactGroupId,
          metricType,
          timezone: businessTimeZone,
        },
      })
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to fetch chart data'
      return rejectWithValue(errorMessage)
    }
  }
)

export const getCreditPullLeaderboard = createAsyncThunk(
  'salesDashboard/getCreditPullLeaderboard',
  async ({ datePreset, startDate, endDate, affiliateId, contactGroupId }, { rejectWithValue }) => {
    try {
      const response = await apiRequest.get('/sales-dashboard/credit-pulls/leaderboard', {
        params: {
          datePreset,
          startDate,
          endDate,
          affiliateId,
          contactGroupId,
          timezone: businessTimeZone,
        },
      })
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to fetch credit pull leaderboard'
      return rejectWithValue(errorMessage)
    }
  }
)

/**
 * Get the records behind one metric card (drill-down).
 * `day`, when present, replaces the period server-side — the panel never intersects the two.
 */
export const getDrilldownRows = createAsyncThunk(
  'salesDashboard/getDrilldownRows',
  async ({ metric, datePreset, startDate, endDate, day, affiliateId, contactGroupId, offset, limit, sortOrder }, { rejectWithValue }) => {
    try {
      const response = await apiRequest.get('/sales-dashboard/drilldown', {
        params: {
          metric,
          datePreset,
          startDate,
          endDate,
          day,
          affiliateId,
          contactGroupId,
          offset,
          limit,
          sortOrder,
          timezone: businessTimeZone,
        },
      })
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to fetch records'
      return rejectWithValue(errorMessage)
    }
  }
)

const initialState = {
  forecasting: {
    averageTerm: null,
    planCount: 0,
    clearanceByPosition: [],
    retentionByPosition: [],
  },
  forecastingLoading: false,
  forecastingError: null,
}

const salesDashboardSlice = createSlice({
  name: 'salesDashboard',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getForecasting.pending, (state) => {
        state.forecastingLoading = true
        state.forecastingError = null
      })
      .addCase(getForecasting.fulfilled, (state, action) => {
        state.forecastingLoading = false
        state.forecasting = action.payload?.data || initialState.forecasting
      })
      .addCase(getForecasting.rejected, (state, action) => {
        state.forecastingLoading = false
        state.forecastingError = action.payload
      })
  },
})

export default salesDashboardSlice.reducer
